import { UpgradeModalViewContext } from '~/App/Services/BillingAnalytics';
import { Service } from '~/types/models';
import { PlasmicPlanCardUid } from '~/types/models/plasmic';
import {
  ShopifyVideoMessagingPlanSlugs,
  VideoMessagingPlanSlugs,
} from '~/types/plans';

export enum UpgradeModalTrigger {
  TeamMemberLimit = 'team-member-limit',
  UpgradeButton = 'upgrade-button',
  GenericTestimonialsUpgrade = 'generic-testimonials-upgrade',
  CustomMessageTemplate = 'custom-message-template',
  MessageTemplateLimit = 'message-template-limit',
  MessageTemplateRemoveBonjoroBadge = 'message-template-remove-bonjoro-badge',
  TestimonialsRemovePoweredByBonjoro = 'testimonials-remove-powered-by-bonjoro',
  NextGenTemplate = 'next-gen-template',
  WorkflowFilters = 'workflow-filters',
  WorkflowLimit = 'workflow-limit',
  RollupLimit = 'rollup-limit',
  CustomDomain = 'custom-domain',
}

/**
 * upgradeModalUid - To determine the plasmic upgrade modal to show
 * service - To determine plan cards to show base on the service
 * viewContext - For tracking purposes
 */
export interface UpgradeModalKeyProps {
  upgradeModalUid: UpgradeModalTrigger;
  service: Service;
  viewContext: UpgradeModalViewContext;
}

export const upgradeModalKeysList: Record<
  UpgradeModalTrigger,
  UpgradeModalKeyProps
> = {
  // For team member limit reached
  [UpgradeModalTrigger.TeamMemberLimit]: {
    upgradeModalUid: UpgradeModalTrigger.TeamMemberLimit,
    service: Service.VideoMessaging,
    viewContext: UpgradeModalViewContext.InviteUpsell,
  },

  // Generic upgrade button on the header
  [UpgradeModalTrigger.UpgradeButton]: {
    upgradeModalUid: UpgradeModalTrigger.UpgradeButton,
    service: Service.VideoMessaging,
    viewContext: UpgradeModalViewContext.GenericUpgrade,
  },

  // Generic upgrade trigger for testimonial module
  [UpgradeModalTrigger.GenericTestimonialsUpgrade]: {
    upgradeModalUid: UpgradeModalTrigger.GenericTestimonialsUpgrade,
    service: Service.Testimonials,
    viewContext: UpgradeModalViewContext.GenericUpgrade,
  },

  // For custom message template module
  [UpgradeModalTrigger.CustomMessageTemplate]: {
    upgradeModalUid: UpgradeModalTrigger.CustomMessageTemplate,
    service: Service.VideoMessaging,
    viewContext: UpgradeModalViewContext.CustomTemplateUpsell,
  },

  // For message template limit reached
  [UpgradeModalTrigger.MessageTemplateLimit]: {
    upgradeModalUid: UpgradeModalTrigger.MessageTemplateLimit,
    service: Service.VideoMessaging,
    viewContext: UpgradeModalViewContext.TemplateLimit,
  },

  // For message template that cannot remove bonjoro badge
  [UpgradeModalTrigger.MessageTemplateRemoveBonjoroBadge]: {
    upgradeModalUid: UpgradeModalTrigger.MessageTemplateRemoveBonjoroBadge,
    service: Service.VideoMessaging,
    viewContext: UpgradeModalViewContext.RemoveBjBadgeUpsell,
  },

  // For testimonials that cannot remove powered by bonjoro
  [UpgradeModalTrigger.TestimonialsRemovePoweredByBonjoro]: {
    upgradeModalUid: UpgradeModalTrigger.TestimonialsRemovePoweredByBonjoro,
    service: Service.Testimonials,
    viewContext: UpgradeModalViewContext.RemoveBjBadgeUpsell,
  },

  // For message template that has no access to next gen template
  [UpgradeModalTrigger.NextGenTemplate]: {
    upgradeModalUid: UpgradeModalTrigger.NextGenTemplate,
    service: Service.VideoMessaging,
    viewContext: UpgradeModalViewContext.NextgenUpsell,
  },

  // For workflow that has no access to filters
  [UpgradeModalTrigger.WorkflowFilters]: {
    upgradeModalUid: UpgradeModalTrigger.WorkflowFilters,
    service: Service.VideoMessaging,
    viewContext: UpgradeModalViewContext.WorkflowFilterUpsell,
  },

  // For workflow limit reached
  [UpgradeModalTrigger.WorkflowLimit]: {
    upgradeModalUid: UpgradeModalTrigger.WorkflowLimit,
    service: Service.VideoMessaging,
    viewContext: UpgradeModalViewContext.IntegrationLimit,
  },

  // For Rollup limit reached
  [UpgradeModalTrigger.RollupLimit]: {
    upgradeModalUid: UpgradeModalTrigger.RollupLimit,
    service: Service.VideoMessaging,
    viewContext: UpgradeModalViewContext.RollupUpsell,
  },

  // Generic upgrade button on the header
  [UpgradeModalTrigger.CustomDomain]: {
    upgradeModalUid: UpgradeModalTrigger.UpgradeButton,
    service: Service.VideoMessaging,
    viewContext: UpgradeModalViewContext.GenericUpgrade,
  },
};

export interface GetSpecificPlasmicPlanCardUidsArgs {
  planSlug?: string;
  trigger?: UpgradeModalTrigger;
}

/**
 * This filters the respective Plasmic Plan Card Uids to Show based on the trigger and plan slug
 * @param GetSpecificPlasmicPlanCardUidsArgs
 * @returns PlasmicPlanCardUid[]
 *
 * TODO: Update this from static into dynamic Smart Upgrade modals.
 * https://bonjoro.atlassian.net/browse/PLG-640
 */
export function getSpecificPlasmicPlanCardUids({
  planSlug,
  trigger,
}: GetSpecificPlasmicPlanCardUidsArgs): PlasmicPlanCardUid[] {
  if (trigger === UpgradeModalTrigger.UpgradeButton) {
    return [
      PlasmicPlanCardUid.VmStarter,
      PlasmicPlanCardUid.VmPro,
      PlasmicPlanCardUid.VmGrrrowth,
      PlasmicPlanCardUid.VmCompany,
    ];
  } else if (trigger === UpgradeModalTrigger.MessageTemplateLimit) {
    switch (planSlug) {
      case VideoMessagingPlanSlugs.FreeMonthly:
      case VideoMessagingPlanSlugs.FreeYearly:
        return [PlasmicPlanCardUid.VmStarter, PlasmicPlanCardUid.VmPro];
      case VideoMessagingPlanSlugs.StarterMonthly:
      case VideoMessagingPlanSlugs.StarterYearly:
        return [PlasmicPlanCardUid.VmPro, PlasmicPlanCardUid.VmGrrrowth];
      case VideoMessagingPlanSlugs.ProMonthly:
      case VideoMessagingPlanSlugs.ProYearly:
        return [PlasmicPlanCardUid.VmGrrrowth, PlasmicPlanCardUid.VmCompany];
      case VideoMessagingPlanSlugs.GrrrowthMonthly:
      case VideoMessagingPlanSlugs.GrrrowthYearly:
        return [PlasmicPlanCardUid.VmCompany];
      default:
        return [PlasmicPlanCardUid.VmStarter, PlasmicPlanCardUid.VmPro];
    }
  } else if (trigger === UpgradeModalTrigger.CustomMessageTemplate) {
    return [
      PlasmicPlanCardUid.VmStarter,
      PlasmicPlanCardUid.VmPro,
      PlasmicPlanCardUid.VmGrrrowth,
    ];
  } else if (trigger === UpgradeModalTrigger.WorkflowFilters) {
    return [PlasmicPlanCardUid.VmStarter, PlasmicPlanCardUid.VmPro];
  } else if (
    trigger === UpgradeModalTrigger.MessageTemplateRemoveBonjoroBadge ||
    trigger === UpgradeModalTrigger.RollupLimit
  ) {
    if (
      planSlug === VideoMessagingPlanSlugs.GrrrowthMonthly ||
      planSlug === VideoMessagingPlanSlugs.GrrrowthYearly
    ) {
      return [PlasmicPlanCardUid.VmCompany];
    }
    return [PlasmicPlanCardUid.VmGrrrowth];
  } else if (
    trigger === UpgradeModalTrigger.TestimonialsRemovePoweredByBonjoro ||
    trigger === UpgradeModalTrigger.GenericTestimonialsUpgrade
  ) {
    return [
      PlasmicPlanCardUid.TmPro,
      PlasmicPlanCardUid.TmGrowth,
      PlasmicPlanCardUid.TmAgency,
    ];
  } else if (trigger == UpgradeModalTrigger.TeamMemberLimit) {
    switch (planSlug) {
      case ShopifyVideoMessagingPlanSlugs.GrrrowthYearly:
      case ShopifyVideoMessagingPlanSlugs.GrrrowthMonthly:
      case ShopifyVideoMessagingPlanSlugs.CompanyMonthly:
      case ShopifyVideoMessagingPlanSlugs.CompanyYearly:
        return [PlasmicPlanCardUid.VmCompany];
      default:
        return [PlasmicPlanCardUid.VmGrrrowth];
    }
  }

  return [
    PlasmicPlanCardUid.VmStarter,
    PlasmicPlanCardUid.VmPro,
    PlasmicPlanCardUid.VmGrrrowth,
    PlasmicPlanCardUid.VmCompany,
  ];
}
